import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Icons } from "../../shared";

export interface SidebarCadastrosSectionProps {
  isMinimized: boolean;
  primaryColor: string;
  primaryColorHover: string;
  getBasePath: () => string;
  onNavigate?: () => void;
}

type SubItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
  /** pathname matches if equals path or starts with this prefix (e.g. subcategories) */
  activePrefix?: string;
};

const subItems: SubItem[] = [
  {
    label: "Categorias",
    path: "/categories",
    icon: <Icons.Categories />,
    activePrefix: "/categories",
  },
  {
    label: "Portadores",
    path: "/bankAccounts",
    icon: <Icons.BankAccount />,
    activePrefix: "/bankAccounts",
  },
  {
    label: "Centro de Custos",
    path: "/cost-centers",
    icon: <Icons.CostCenter />,
    activePrefix: "/cost-centers",
  },
];

function pathMatches(
  pathname: string,
  base: string,
  path: string,
  activePrefix?: string
): boolean {
  const normalized =
    pathname === base ? "/" : pathname.slice(base.length) || pathname;
  if (activePrefix) {
    return (
      normalized === path ||
      normalized.startsWith(`${activePrefix}/`) ||
      normalized === activePrefix
    );
  }
  return normalized === path;
}

export const SidebarCadastrosSection: React.FC<SidebarCadastrosSectionProps> = ({
  isMinimized,
  primaryColor,
  primaryColorHover,
  getBasePath,
  onNavigate,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const base = getBasePath();

  const anyChildActive = useMemo(
    () =>
      subItems.some((item) =>
        pathMatches(location.pathname, base, item.path, item.activePrefix)
      ),
    [location.pathname, base]
  );

  const [expanded, setExpanded] = useState(anyChildActive);

  useEffect(() => {
    if (anyChildActive) setExpanded(true);
  }, [anyChildActive]);

  const navigateTo = (path: string) => {
    navigate(`${base}${path}`);
    onNavigate?.();
  };

  const toggleExpanded = () => setExpanded((e) => !e);

  // Sidebar minimizado: só ícones, sem agrupamento visual
  if (isMinimized) {
    return (
      <div className="space-y-2 flex flex-col items-center">
        {subItems.map((item) => {
          const active = pathMatches(
            location.pathname,
            base,
            item.path,
            item.activePrefix
          );
          return (
            <button
              key={item.path}
              type="button"
              title={item.label}
              className={`w-12 h-12 flex items-center justify-center rounded-full font-medium transition-all duration-200 ${
                active ? "text-white border-l-4" : "text-gray-300 hover:bg-[#2D3748]"
              }`}
              style={{
                backgroundColor: active ? primaryColorHover : "transparent",
                borderLeftColor: active ? primaryColor : "transparent",
                color: active ? "#fff" : "#D1D5DB",
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = primaryColorHover;
                  e.currentTarget.style.color = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#D1D5DB";
                }
              }}
              onClick={() => navigateTo(item.path)}
            >
              <span
                className="flex items-center [&>.MuiSvgIcon-root]:text-[1.25rem]"
                style={active ? { color: primaryColor } : undefined}
              >
                {item.icon}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={toggleExpanded}
        className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-medium transition-all duration-200 text-sm sm:text-base text-gray-300 hover:bg-[#2D3748] ${
          anyChildActive && !expanded ? "bg-[#2D3748]/50" : ""
        }`}
        aria-expanded={expanded}
      >
        <span className="flex items-center text-base sm:text-lg [&>.MuiSvgIcon-root]:text-[1.25rem] text-[#63B3ED]">
          <Icons.Folder />
        </span>
        <span className="truncate flex-1 text-left">Cadastros</span>
        <span
          className={`text-gray-400 text-xs transition-transform flex-shrink-0 ${
            expanded ? "rotate-180" : ""
          }`}
          aria-hidden
        >
          ▼
        </span>
      </button>

      {expanded && (
        <div className="mt-1 ml-1 pl-2 border-l-2 border-[#374151] space-y-1">
          {subItems.map((item) => {
            const active = pathMatches(
              location.pathname,
              base,
              item.path,
              item.activePrefix
            );
            return (
              <button
                key={item.path}
                type="button"
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  active
                    ? "text-white border-l-4"
                    : "text-gray-400 hover:bg-[#2D3748] hover:text-gray-200"
                }`}
                style={{
                  backgroundColor: active ? primaryColorHover : "transparent",
                  borderLeftColor: active ? primaryColor : "transparent",
                  color: active ? "#fff" : undefined,
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.backgroundColor = primaryColorHover;
                    e.currentTarget.style.color = "#fff";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#9CA3AF";
                  }
                }}
                onClick={() => navigateTo(item.path)}
              >
                <span
                  className="flex items-center [&>.MuiSvgIcon-root]:text-[1.1rem]"
                  style={active ? { color: primaryColor } : undefined}
                >
                  {item.icon}
                </span>
                <span className="truncate text-left">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
